import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { 
  MessageSquare, 
  Send, 
  Search, 
  Plus, 
  User, 
  Clock, 
  Check, 
  CheckCheck,
  Paperclip,
  MoreVertical,
  BookOpen,
  GraduationCap,
  Shield,
  Wifi,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const MessagesPage = ({ onMenuClick }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  const { 
    isConnected, 
    chatMessages, 
    conversationUpdates, 
    typingIndicators,
    clearChatMessage,
    clearConversationUpdate,
    updateChatUnreadCount
  } = useWebSocket();

  // Fetch conversations
  useEffect(() => {
    fetchConversations();
    fetchContacts();
  }, []);

  // Handle incoming chat messages
  useEffect(() => {
    if (chatMessages.length > 0) {
      const latestMessage = chatMessages[chatMessages.length - 1];
      
      // If this message belongs to the current conversation, add it
      if (selectedConversation && latestMessage.conversation_id === selectedConversation.id) {
        setMessages(prev => {
          // Check if message already exists
          if (prev.some(m => m.id === latestMessage.id)) return prev;
          return [...prev, {
            ...latestMessage,
            is_own: false
          }];
        });
        scrollToBottom();
        // Mark as read
        markConversationRead(selectedConversation.id);
      }
      
      // Update conversation list
      fetchConversations();
      clearChatMessage(latestMessage.id);
    }
  }, [chatMessages, selectedConversation]);

  // Handle conversation updates
  useEffect(() => {
    conversationUpdates.forEach(update => {
      setConversations(prev => prev.map(conv => {
        if (conv.id === update.conversation_id) {
          return {
            ...conv,
            last_message: { 
              content: update.last_message, 
              created_at: update.last_message_at 
            },
            unread_count: update.unread_count,
            last_message_at: update.last_message_at
          };
        }
        return conv;
      }));
      clearConversationUpdate(update.conversation_id);
    });
  }, [conversationUpdates]);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
      
      // Update total unread
      const totalUnread = response.data.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      updateChatUnreadCount(totalUnread);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/messages/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    setMessagesLoading(true);
    try {
      const response = await api.get(`/messages/conversations/${conversationId}`);
      // Normalize: API returns `is_mine`, the UI uses `is_own`; expose sender_name from sender object
      const normalized = response.data.map((m) => ({
        ...m,
        is_own: m.is_mine !== undefined ? m.is_mine : m.is_own,
        sender_name: m.sender_name || m.sender?.name
      }));
      setMessages(normalized);
      scrollToBottom();
      // Messages are marked as read automatically by the API
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      ));
      // Emit event to refresh sidebar badges
      window.dispatchEvent(new CustomEvent('refreshBadges'));
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const markConversationRead = async (conversationId) => {
    // Handled by fetchMessages now
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage('');

    // Optimistically add message
    const tempMessage = {
      id: `temp-${Date.now()}`,
      content: messageContent,
      sender_name: 'Vous',
      is_own: true,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const formData = new FormData();
      formData.append('message', messageContent);
      
      const response = await api.post(`/messages/conversations/${selectedConversation.id}/reply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Replace temp message with real one
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? { ...m, id: response.data.message_id } : m
      ));
      
      // Update conversation
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleStartNewChat = async (contact) => {
    try {
      const formData = new FormData();
      formData.append('recipient_id', contact.id);
      // No initial message: just open/create the conversation so the user can type
      
      const response = await api.post('/messages/conversations', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setShowNewChat(false);
      fetchConversations();
      
      // Select the new/existing conversation
      const convResponse = await api.get('/messages/conversations');
      const newConv = convResponse.data.find(c => c.id === response.data.conversation_id);
      if (newConv) {
        handleSelectConversation(newConv);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Erreur lors de la création de la conversation');
    }
  };

  const handleTyping = useCallback(() => {
    // Typing indicators disabled for now
  }, [selectedConversation]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    
    if (hours < 24) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    if (hours < 48) {
      return 'Hier';
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'trainer':
        return <GraduationCap className="w-3 h-3" />;
      case 'cfa_admin':
        return <Shield className="w-3 h-3" />;
      default:
        return <User className="w-3 h-3" />;
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      trainer: { label: 'Formateur', color: 'bg-blue-100 text-blue-700' },
      cfa_admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
      student: { label: 'Étudiant', color: 'bg-emerald-100 text-emerald-700' }
    };
    const c = config[role] || { label: role, color: 'bg-slate-100 text-slate-700' };
    return <Badge className={`${c.color} text-xs`}>{c.label}</Badge>;
  };

  // Helper to get participant from conversation (API returns other_participant)
  const getParticipant = (conv) => conv?.other_participant || conv?.participant || {};

  const filteredConversations = conversations.filter(c => {
    const participant = getParticipant(c);
    return participant?.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredContacts = contacts.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentTyping = selectedConversation ? typingIndicators[selectedConversation.id] : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Messages" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">Messagerie</h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm flex items-center gap-2">
              Échangez avec vos formateurs et l'administration
              {isConnected && (
                <span className="flex items-center gap-1 text-emerald-600">
                  <Wifi className="w-3 h-3" />
                  <span className="text-xs">En ligne</span>
                </span>
              )}
            </p>
          </div>
          <Button onClick={() => setShowNewChat(true)} data-testid="new-chat-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle conversation
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)]">
          {/* Conversations List */}
          <Card className="lg:col-span-1 flex flex-col">
            <CardContent className="p-4 flex flex-col h-full">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher une conversation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="conversation-search"
                />
              </div>
              
              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Aucune conversation</p>
                    <Button 
                      variant="link" 
                      className="mt-2" 
                      onClick={() => setShowNewChat(true)}
                    >
                      Démarrer une conversation
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredConversations.map((conv) => {
                      const participant = getParticipant(conv);
                      return (
                      <div
                        key={conv.id}
                        className={`p-3 rounded-xl cursor-pointer transition-all ${
                          selectedConversation?.id === conv.id
                            ? 'bg-violet-100 dark:bg-violet-900/30 border-violet-300'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                        } ${conv.unread_count > 0 ? 'border-l-4 border-violet-600' : ''}`}
                        onClick={() => handleSelectConversation(conv)}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                            {getInitials(participant.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`font-medium text-sm truncate ${conv.unread_count > 0 ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                                {participant.name || 'Utilisateur'}
                              </span>
                              {conv.last_message?.created_at && (
                                <span className="text-xs text-slate-500">
                                  {formatTime(conv.last_message.created_at)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {getRoleBadge(participant.role)}
                              {conv.context && (
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                  <BookOpen className="w-3 h-3" />
                                  {conv.context.name}
                                </span>
                              )}
                            </div>
                            {conv.last_message?.content && (
                              <p className={`text-xs mt-1 truncate ${conv.unread_count > 0 ? 'text-slate-800 dark:text-slate-200 font-medium' : 'text-slate-500'}`}>
                                {conv.last_message.content}
                              </p>
                            )}
                          </div>
                          {conv.unread_count > 0 && (
                            <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] text-white font-bold">{conv.unread_count}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )})}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <Card className="lg:col-span-2 flex flex-col">
            {selectedConversation ? (() => {
              const selectedParticipant = getParticipant(selectedConversation);
              return (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-medium">
                        {getInitials(selectedParticipant.name)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                          {selectedParticipant.name || 'Utilisateur'}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {getRoleIcon(selectedParticipant.role)}
                          <span>{getRoleBadge(selectedParticipant.role)}</span>
                          {selectedConversation.context && (
                            <>
                              <span>•</span>
                              <span>{selectedConversation.context.name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Messages */}
                <CardContent className="flex-1 p-4 overflow-hidden">
                  <ScrollArea className="h-full pr-4">
                    {messagesLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-slate-500">
                        <MessageSquare className="w-12 h-12 mb-3 text-slate-400" />
                        <p>Commencez la conversation</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((msg, idx) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.is_own ? 'justify-end' : 'justify-start'}`}
                            data-testid={`message-${msg.id}`}
                          >
                            <div className={`flex gap-2 max-w-[75%] ${msg.is_own ? 'flex-row-reverse' : ''}`}>
                              {!msg.is_own && (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                                  {getInitials(msg.sender_name || msg.sender?.name || selectedParticipant.name)}
                                </div>
                              )}
                              <div>
                                <div
                                  className={`rounded-2xl px-4 py-2.5 ${
                                    msg.is_own
                                      ? 'bg-violet-600 text-white rounded-br-md'
                                      : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-bl-md'
                                  }`}
                                >
                                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                  {msg.attachment_url && (
                                    <a 
                                      href={msg.attachment_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-1 text-xs mt-2 ${msg.is_own ? 'text-violet-200' : 'text-violet-600'}`}
                                    >
                                      <Paperclip className="w-3 h-3" />
                                      {msg.attachment_name || 'Pièce jointe'}
                                    </a>
                                  )}
                                </div>
                                <div className={`flex items-center gap-1 mt-1 text-xs text-slate-400 ${msg.is_own ? 'justify-end' : ''}`}>
                                  <Clock className="w-3 h-3" />
                                  {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  {msg.is_own && (
                                    msg.is_read ? (
                                      <CheckCheck className="w-3 h-3 text-violet-500" title="Lu" />
                                    ) : (
                                      <Check className="w-3 h-3 text-slate-400" title="Envoyé" />
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {currentTyping && (
                          <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <div className="flex gap-1">
                              <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                            <span>{currentTyping.user_name} écrit...</span>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>

                {/* Message Input */}
                <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="Écrivez votre message..."
                      disabled={sending}
                      data-testid="message-input"
                      className="flex-1"
                    />
                    <Button type="submit" disabled={!newMessage.trim() || sending} data-testid="send-message-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </>
            )})() : (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <MessageSquare className="w-16 h-16 mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Sélectionnez une conversation
                </h3>
                <p className="text-sm text-center max-w-xs">
                  Choisissez une conversation dans la liste ou démarrez une nouvelle discussion
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4" 
                  onClick={() => setShowNewChat(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle conversation
                </Button>
              </div>
            )}
          </Card>
        </div>
      </main>

      {/* New Chat Dialog */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle conversation</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un contact..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="contact-search-input"
              />
            </div>
            <ScrollArea className="h-[350px]">
              {filteredContacts.length === 0 ? (
                <div className="text-center py-8 text-slate-500" data-testid="no-contacts-message">
                  <User className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm">Aucun contact disponible</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Groupe: Administration */}
                  {filteredContacts.filter(c => c.role === 'cfa_admin').length > 0 && (
                    <div data-testid="contacts-group-admin">
                      <div className="flex items-center gap-2 px-2 mb-2">
                        <Shield className="w-4 h-4 text-purple-500" />
                        <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">
                          Administration
                        </span>
                        <span className="text-xs text-slate-400">
                          ({filteredContacts.filter(c => c.role === 'cfa_admin').length})
                        </span>
                      </div>
                      <div className="space-y-1">
                        {filteredContacts.filter(c => c.role === 'cfa_admin').map((contact) => (
                          <div
                            key={contact.id}
                            className="p-3 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer transition-colors border border-transparent hover:border-purple-200 dark:hover:border-purple-800"
                            onClick={() => handleStartNewChat(contact)}
                            data-testid={`contact-${contact.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white font-medium text-sm">
                                {getInitials(contact.name)}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-slate-900 dark:text-white">{contact.name}</p>
                                <p className="text-xs text-slate-500">{contact.email}</p>
                              </div>
                              <Shield className="w-4 h-4 text-purple-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Groupe: Formateurs */}
                  {filteredContacts.filter(c => c.role === 'trainer').length > 0 && (
                    <div data-testid="contacts-group-trainers">
                      <div className="flex items-center gap-2 px-2 mb-2">
                        <GraduationCap className="w-4 h-4 text-blue-500" />
                        <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                          Formateurs
                        </span>
                        <span className="text-xs text-slate-400">
                          ({filteredContacts.filter(c => c.role === 'trainer').length})
                        </span>
                      </div>
                      <div className="space-y-1">
                        {filteredContacts.filter(c => c.role === 'trainer').map((contact) => (
                          <div
                            key={contact.id}
                            className="p-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
                            onClick={() => handleStartNewChat(contact)}
                            data-testid={`contact-${contact.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-medium text-sm">
                                {getInitials(contact.name)}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-slate-900 dark:text-white">{contact.name}</p>
                                <p className="text-xs text-slate-500">
                                  {contact.specialization || contact.email}
                                </p>
                              </div>
                              <GraduationCap className="w-4 h-4 text-blue-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Groupe: Étudiants */}
                  {filteredContacts.filter(c => c.role === 'student').length > 0 && (
                    <div data-testid="contacts-group-students">
                      <div className="flex items-center gap-2 px-2 mb-2">
                        <User className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                          Étudiants
                        </span>
                        <span className="text-xs text-slate-400">
                          ({filteredContacts.filter(c => c.role === 'student').length})
                        </span>
                      </div>
                      <div className="space-y-1">
                        {filteredContacts.filter(c => c.role === 'student').map((contact) => (
                          <div
                            key={contact.id}
                            className="p-3 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 cursor-pointer transition-colors border border-transparent hover:border-emerald-200 dark:hover:border-emerald-800"
                            onClick={() => handleStartNewChat(contact)}
                            data-testid={`contact-${contact.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-medium text-sm">
                                {getInitials(contact.name)}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-slate-900 dark:text-white">{contact.name}</p>
                                <p className="text-xs text-slate-500">
                                  {contact.student_number ? `N° ${contact.student_number}` : contact.email}
                                </p>
                              </div>
                              <User className="w-4 h-4 text-emerald-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
